import React, { useState, useEffect, useContext, useMemo } from "react";
import { Card, Page, ResourceList, ResourceItem, Filters, Select, Icon, Text } from "@shopify/polaris";
import { TitleBar } from "@shopify/app-bridge-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import pluralize from "../../../../utilities/pluralize";
import { AppContext } from "../../../../components/global/appContext";

import { useGetTags, useBulkDeleteTags } from "../../../../hooks/queries/tags";
import {mobileTitle} from "../../../../utilities/mobile";

export default function TagsIndex(props) {
  const { t } = useTranslation();
  const appContext = useContext(AppContext);
  const navigate = useNavigate();

  const [filteredTags, setFilteredTags] = useState(null);
  const [queryValue, setQueryValue] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedTagGroup, setSelectedTagGroup] = useState("any");

  const { data, isError, isLoading } = useGetTags();
  const bulkDeleteTagsMutation = useBulkDeleteTags({
    onSuccess: (data) => {
      setSelectedTags([]);
      // setTags(res.data.tags);
      // setTagGroups(res.data.all_groups);
      appContext.setMessage(t("settings.tags.messages.deletedSuccessfully"));
    },
    onError: (error) => {
      appContext.setMessage(t("settings.tags.messages.couldntDelete"), true);
    },
  });

  const tags = useMemo(() => data?.tags || [], [data]);
  const tagGroups = useMemo(() => data?.all_groups || [], [data]);

  // Update filtered tags whenever tags, query value or selected tag group change
  useEffect(() => {
    if (!tags) return;
    let filteredTags = [...tags];
    // Filter by query
    if (queryValue !== "") {
      const queryFilterRegex = new RegExp(queryValue, "i");
      filteredTags = filteredTags.filter((tag) =>
        tag.title.match(queryFilterRegex)
      );
    }
    // Filter by group
    if (selectedTagGroup !== "any") {
      filteredTags = filteredTags.filter(
        (tag) => tag.group == selectedTagGroup
      );
    }
    // Set filtered tags
    setFilteredTags(filteredTags);
  }, [queryValue, selectedTagGroup, tags]);

  if (isError) return <p>{t("settings.tags.error")}</p>;
  // if (isLoading) return <p>Skeleton</p>;

  const tagGroupOptions = [{ label: t("settings.tags.filters.anyGroup"), value: "any" }].concat(
    tagGroups.map((group) => {
      return { label: group, value: group };
    })
  );

  // Filter states

  // Update applied filters
  const appliedFilters = [];
  selectedTagGroup !== "any" &&
    appliedFilters.push({
      key: "group",
      label: t("settings.tags.appliedFilters.inGroup", { group: selectedTagGroup.toLowerCase() }),
      onRemove: () => {
        setSelectedTagGroup("any");
      },
    });
  queryValue !== "" &&
    appliedFilters.push({
      key: "search",
      label: t("settings.tags.appliedFilters.nameContains", { query: queryValue }),
      onRemove: () => {
        setQueryValue("");
      },
    });

  const filters = [
    {
      key: "group",
      label: t("settings.tags.filters.inGroup"),
      filter: (
        <div style={{ minWidth: "150px" }}>
          <Select
            label={t("settings.tags.filters.tagGroup")}
            labelHidden
            options={tagGroupOptions}
            onChange={setSelectedTagGroup}
            value={selectedTagGroup}
          />
        </div>
      ),
      shortcut: true,
    },
  ];

  const filterControl = (
    <Filters
      disabled={isLoading}
      queryValue={queryValue}
      filters={filters}
      appliedFilters={appliedFilters}
      onQueryChange={setQueryValue}
      onQueryClear={() => setQueryValue("")}
      onClearAll={() => {}}
    />
  );

  function renderTag(tag) {
    const { id, title, description, group, image, show_on_filter, url } = tag;
    return (
      <ResourceItem
        id={id}
        url={url}
        onClick={() => navigate(url)}
        // media={<Icon backdrop={true} color="subdued" source={show_on_filter ? ViewMinor : HideMinor}/>}
        verticalAlignment="center"
      >
        <h3>
          <Text  variant="bodyMd" as="span" fontWeight="semibold">{title}</Text>
        </h3>
        <Text  variant="bodyMd" as="span" color="subdued">{group}</Text>
      </ResourceItem>
    );
  }
  return (
    <Page>
      <TitleBar
        title={mobileTitle(t("settings.tags.title"), t("settings.common.breadcrumbs.settings"))}
        primaryAction={{ content: t("settings.tags.addTag"), url: "/settings/tags/new" }}
        breadcrumbs={[{ content: t("settings.common.breadcrumbs.settings") }]}
      />
      <Card>
        <ResourceList
          loading={isLoading}
          resourceName={{ 
            singular: t("settings.tags.resourceName.singular"), 
            plural: t("settings.tags.resourceName.plural") 
          }}
          filterControl={filterControl}
          selectedItems={selectedTags}
          onSelectionChange={setSelectedTags}
          bulkActions={[
            {
              content: `${t("common.delete")} ${pluralize(selectedTags.length, [
                t("settings.tags.resourceName.singular"),
                t("settings.tags.resourceName.plural"),
              ])}`,
              destructive: true,
              onAction: () => bulkDeleteTagsMutation.mutate(selectedTags),
              loading: bulkDeleteTagsMutation.isLoading,
            },
          ]}
          items={filteredTags || tags || []}
          renderItem={renderTag}
        />
      </Card>
    </Page>
  );
}
